import { FetchResult, InternalRefetchQueriesInclude } from '@apollo/client';
import { AnalyticsContext } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useContext } from 'react';
import {
  AutoPopulateAccommodationPlacesPayloadV5,
  ScoringPriorityField,
  useAutoPopulateAccommodationPlacesV5Mutation,
  Maybe,
  AutopopulateAccommodationPlacesScenario,
} from '@graphql/generated';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';

export interface CallAutoPopulateAccommodationPlacesV5Payload
  extends Readonly<{
    eventId?: string | null;
    location: Readonly<{ latitude: number; longitude: number }>;
    scoringPriorityFields: ReadonlyArray<ScoringPriorityField>;
    context: string;
    searchRadiusKm: number;
    venueGooglePlaceId?: string;
    scenario?: AutopopulateAccommodationPlacesScenario;
  }> {}

export type AutoPopulateV5Response = Readonly<{
  idempotentId?: string;
  status?: string;
  error?: Maybe<string>;
}>;

/**
 * Custom hook to call the auto-populate accommodation places v5 mutation.
 * @param refetchQueries - Optional refetch queries to include in the mutation.
 * @returns A tuple containing the callAutoPopulateAccommodationPlacesV5 function and the loading state.
 */
export const useAutoPopulateAccommodationPlacesV5 = (refetchQueries?: ((result: FetchResult) => InternalRefetchQueriesInclude) | InternalRefetchQueriesInclude) => {
  const analytics = useContext(AnalyticsContext);
  const { language: locale } = useLocaleContext();

  const [autoPopulateAccommodationPlacesV5, { loading }] = useAutoPopulateAccommodationPlacesV5Mutation();

  const callAutoPopulateAccommodationPlacesV5 = useEventCallback(async (payloadArgs: CallAutoPopulateAccommodationPlacesV5Payload): Promise<AutoPopulateV5Response> => {
    const { eventId, location, scoringPriorityFields, context, searchRadiusKm, venueGooglePlaceId, scenario } = payloadArgs;

    if (!eventId) {
      const error = new Error('Missing event id');
      addError(error, {
        context,
        latitude: location?.latitude,
        longitude: location?.longitude,
        isAsync: true,
        scenario,
      });
      return { error: error.message };
    }

    const payload: AutoPopulateAccommodationPlacesPayloadV5 = {
      eventId,
      force: true,
      location,
      searchRadiusKm,
      locale,
      scoringPriorityFields: [...scoringPriorityFields],
      venueGooglePlaceId: venueGooglePlaceId,
      scenario,
    };

    try {
      const response = await autoPopulateAccommodationPlacesV5({ variables: { payload }, refetchQueries });

      if (!response.data || !response.data.autoPopulateAccommodationPlacesV5) {
        const error = 'No response data';
        addError(new Error(error), {
          context,
          eventId: payload.eventId,
          latitude: payload.location?.latitude,
          longitude: payload.location?.longitude,
          isAsync: true,
          scenario,
        });
        return { error };
      }

      const { status, error } = response.data.autoPopulateAccommodationPlacesV5;

      analytics.track({
        category: 'accommodations',
        action: `TravelButtonInteracted`,
        label: 'autoPopulateAccommodationPlaces',
        extraInfo: {
          name: `autoPopulateAccommodationPlaces`,
          eventId: payload.eventId,
          latitude: payload.location?.latitude,
          longitude: payload.location?.longitude,
          isAsync: true,
          scenario,
          source: context,
          status,
        },
      });

      return { status, error };
    } catch (error) {
      const { errorExplanation, statusCode, isTimeout } = parseNetworkErrorDetails(error);
      addError(errorExplanation, {
        context,
        eventId: payload.eventId,
        latitude: payload.location?.latitude,
        longitude: payload.location?.longitude,
        isAsync: true,
        scenario,
        statusCode,
        isTimeout,
        originalError: error,
      });
      throw error;
    }
  });

  return { callAutoPopulateAccommodationPlacesV5, loading };
};
